import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Calendar, Tag, ArrowLeft, ArrowRight, Phone, BookOpen, Loader2, Share2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabel = (v) => {
  if (v === 'local-news') return 'Local News';
  if (v === 'industry-tips') return 'Industry Tips';
  return 'Article';
};

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await axios.get(`${API}/blog/posts/${slug}`);
        if (res.data.success) {
          setPost(res.data.post);
          setRelated(res.data.related || []);
        } else {
          setNotFound(true);
        }
      } catch (e) {
        console.error('Error loading post:', e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url });
      } catch {
        /* cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
      } catch {
        /* noop */
      }
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 text-emerald-600 animate-spin" />
      </section>
    );
  }

  if (notFound || !post) {
    return (
      <section className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-10 text-center">
            <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Article Not Found</h2>
            <p className="text-slate-600 mb-6">
              This article may have been moved or is no longer available.
            </p>
            <Button onClick={() => navigate('/blog')} className="bg-emerald-600 hover:bg-emerald-700">
              Back to Blog
            </Button>
          </CardContent>
        </Card>
      </section>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image || 'https://aisexpress.co.uk/android-chrome-512x512.png',
    author: { '@type': 'Organization', name: 'AIS Express' },
    publisher: {
      '@type': 'Organization',
      name: 'AIS Express',
      logo: { '@type': 'ImageObject', url: 'https://aisexpress.co.uk/android-chrome-512x512.png' },
    },
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at || post.published_at || post.created_at,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://aisexpress.co.uk/blog/${post.slug}` },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://aisexpress.co.uk' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://aisexpress.co.uk/blog' },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: `https://aisexpress.co.uk/blog/${post.slug}`,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | AIS Express Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={(post.tags || []).join(', ')} />
        <link rel="canonical" href={`https://aisexpress.co.uk/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={`https://aisexpress.co.uk/blog/${post.slug}`} />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <section className="min-h-screen bg-white" data-testid="blog-post-page">
        {/* Cover image */}
        {post.cover_image && (
          <div
            className="h-64 md:h-96 bg-cover bg-center"
            style={{ backgroundImage: `url(${post.cover_image})` }}
          />
        )}

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 max-w-3xl">
          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="mb-6 text-slate-600 hover:text-emerald-700"
            data-testid="blog-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to all articles
          </Button>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">
              <Tag className="h-3 w-3 mr-1" />
              {categoryLabel(post.category)}
            </span>
            <span className="inline-flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {post.published_at || post.created_at}
            </span>
            <button
              onClick={handleShare}
              className="inline-flex items-center text-emerald-700 hover:text-emerald-800 font-medium"
              data-testid="blog-share-btn"
            >
              <Share2 className="h-4 w-4 mr-1" /> Share
            </button>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            {post.title}
          </h1>

          {/* Excerpt */}
          <p className="text-lg text-slate-600 mb-8 leading-relaxed border-l-4 border-emerald-500 pl-4 italic">
            {post.excerpt}
          </p>

          {/* Article content */}
          <article
            className="blog-article-content text-slate-700"
            dangerouslySetInnerHTML={{ __html: post.content }}
            data-testid="blog-content"
          />
          <style>{`
            .blog-article-content h2 { font-size: 1.75rem; font-weight: 700; color: #0f172a; margin-top: 2rem; margin-bottom: 0.75rem; line-height: 1.3; }
            .blog-article-content h3 { font-size: 1.35rem; font-weight: 600; color: #0f172a; margin-top: 1.5rem; margin-bottom: 0.5rem; }
            .blog-article-content p { margin-bottom: 1rem; line-height: 1.75; font-size: 1.05rem; }
            .blog-article-content ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
            .blog-article-content ol { list-style: decimal; padding-left: 1.5rem; margin-bottom: 1rem; }
            .blog-article-content li { margin-bottom: 0.35rem; line-height: 1.65; }
            .blog-article-content a { color: #047857; text-decoration: underline; font-weight: 500; }
            .blog-article-content a:hover { color: #065f46; }
            .blog-article-content strong { color: #0f172a; font-weight: 600; }
            .blog-article-content blockquote { border-left: 4px solid #10b981; padding-left: 1rem; font-style: italic; color: #475569; margin: 1.25rem 0; }
            .blog-article-content img { border-radius: 0.5rem; margin: 1.25rem 0; max-width: 100%; height: auto; }
          `}</style>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 pt-6 border-t border-slate-200">
              <div className="flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1 text-xs bg-slate-100 text-slate-700 rounded-full"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white text-center shadow-xl">
            <h3 className="text-2xl font-bold mb-2">Need reliable same-day delivery?</h3>
            <p className="text-emerald-50 mb-5">
              Get an instant price estimate or call our team 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/book')}
                size="lg"
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold"
              >
                Book a Delivery
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                <a href="tel:01344508095">
                  <Phone className="h-4 w-4 mr-2" /> 01344 508095
                </a>
              </Button>
            </div>
          </div>

          {/* Related posts */}
          {related.length > 0 && (
            <div className="mt-14">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((r) => (
                  <Card
                    key={r.id}
                    className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => navigate(`/blog/${r.slug}`)}
                  >
                    <CardContent className="p-5">
                      <span className="text-xs text-emerald-700 font-semibold">
                        {categoryLabel(r.category)}
                      </span>
                      <h4 className="text-lg font-bold text-slate-900 mt-1 mb-2 line-clamp-2">
                        {r.title}
                      </h4>
                      <p className="text-sm text-slate-600 line-clamp-2">{r.excerpt}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default BlogPost;
